export const BADGE_SHAPE_KEYS = [
    'bookmark',
    'star6',
    'drop',
    'cloud',
    'ticket',
    'sun',
    'bubble',
    'arch',
    'diamond',
    'quatrefoil',
    'shield',
    'ringmedal',
    'square',
    'triangle',
    'wedge',
    'circle',
    'star5',
    'heart',
] as const
export type BadgeShapeKey = (typeof BADGE_SHAPE_KEYS)[number]

export const BADGE_PALETTE_KEYS = [
    'yellow',
    'purple',
    'blue',
    'mint',
    'coral',
    'pink',
    'nope',
    'play',
] as const
export type BadgePaletteKey = (typeof BADGE_PALETTE_KEYS)[number]

export const BADGE_TIERS = ['bronze', 'silver', 'gold', 'crystal'] as const
export type BadgeTier = (typeof BADGE_TIERS)[number]

export interface BadgeLabel {
    et: string
    en: string
}

export interface BadgeShape {
    label: BadgeLabel
    /** Outline path on the 200×200 grid. */
    d: string
    /** [cx, cy, scale]: optical centre of the free area and icon scale. */
    icon: [number, number, number]
    /** [rim, ring, line] stroke widths: metal rim, enamel ring, inner metal line. */
    inset: [number, number, number]
    /** No inner metal line (anniversary ring draws year arcs instead). */
    noLine?: boolean
}

export interface BadgeEnamel {
    ring: string
    field: string
}

export interface BadgeIconDef {
    label: BadgeLabel
    /** Optical centre on the 64 grid. */
    center: [number, number]
    /** Inner SVG markup on the 64 grid; `{fill}` marks solid parts. */
    svg: string
}

/** Placeholder inside icon markup that the renderer replaces with the metal paint. */
export const ICON_FILL_TOKEN = '{fill}'

/** Everything the renderer needs; the server resolves icons before this is built. */
export interface BadgeArtSpec {
    shape: BadgeShapeKey
    palette: BadgePaletteKey
    tier: BadgeTier
    /** Resolved 64-grid markup (custom set or Lucide), null = no icon. */
    iconMarkup: string | null
    /** Anniversary badges: the year count drawn as number + ring arcs. */
    anniversaryYears: number | null
}

export type BadgeArtState = 'active' | 'slot'

/** Theme-dependent colours; the client passes `var(--badge-…)`, the server passes literals. */
export interface BadgeArtColors {
    shadow: string
    slot: string
    slotInk: string
    slotHi: string
    slotShade: string
}

export interface BadgeArtRenderOptions {
    /** Prefix for every SVG id, unique per badge instance on a page. */
    idPrefix: string
    state: BadgeArtState
    colors: BadgeArtColors
}
